import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Trash2, Loader2, AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClueLanguageFlagsEditor } from "./ClueLanguageFlagsEditor";

interface WordResult {
  id: number;
  word: string;
  word_upper: string;
  length: number;
  clues: ClueResult[];
}

interface ClueResult {
  id: number;
  clue: string;
  clue_upper: string;
  word_id: number;
  language: number;
}

export function DatabaseManagementAdmin() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<WordResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    type: 'word' | 'clue';
    id: number;
    name: string;
    wordId?: number;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const searchWords = async () => {
    if (!searchTerm.trim()) {
      toast.error("Ange ett sökord");
      return;
    }

    setLoading(true);
    try {
      const searchUpper = searchTerm.toUpperCase();
      
      // Search for words
      const { data: words, error: wordsError } = await supabase
        .from('words')
        .select('id, word, word_upper, length')
        .or(`word_upper.ilike.%${searchUpper}%,word.ilike.%${searchTerm}%`)
        .limit(50);

      if (wordsError) throw wordsError;

      // Get clues for found words
      const results: WordResult[] = [];
      
      for (const word of words || []) {
        const { data: clues } = await supabase
          .from('clues')
          .select('id, clue, clue_upper, word_id, language')
          .eq('word_id', word.id);
        
        results.push({
          ...word,
          clues: clues || []
        });
      }

      // Also search in clues directly
      const { data: clueMatches, error: cluesError } = await supabase
        .from('clues')
        .select('id, clue, clue_upper, word_id, language')
        .or(`clue_upper.ilike.%${searchUpper}%,clue.ilike.%${searchTerm}%`)
        .limit(50);

      if (cluesError) throw cluesError;

      // Add words from clue matches if not already in results
      const existingWordIds = new Set(results.map(r => r.id));
      const additionalWordIds = [...new Set((clueMatches || []).map(c => c.word_id))].filter(id => !existingWordIds.has(id));
      
      for (const wordId of additionalWordIds) {
        const { data: word } = await supabase
          .from('words')
          .select('id, word, word_upper, length')
          .eq('id', wordId)
          .single();
        
        if (word) {
          const { data: clues } = await supabase
            .from('clues')
            .select('id, clue, clue_upper, word_id, language')
            .eq('word_id', word.id);
          
          results.push({
            ...word,
            clues: clues || []
          });
        }
      }

      setSearchResults(results);
      
      if (results.length === 0) {
        toast.info("Inga resultat hittades");
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error(`Sökfel: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWord = async () => {
    if (!deleteDialog || deleteDialog.type !== 'word') return;
    
    setDeleting(true);
    try {
      // First delete all clues for this word
      const { error: cluesError } = await supabase
        .from('clues')
        .delete()
        .eq('word_id', deleteDialog.id);

      if (cluesError) throw cluesError;

      // Then delete the word
      const { error: wordError } = await supabase
        .from('words')
        .delete()
        .eq('id', deleteDialog.id);

      if (wordError) throw wordError;

      toast.success(`Ordet "${deleteDialog.name}" och alla dess ledtrådar raderades`);
      
      // Update results
      setSearchResults(prev => prev.filter(w => w.id !== deleteDialog.id));
    } catch (error: any) {
      console.error('Delete word error:', error);
      toast.error(`Kunde inte radera: ${error.message}`);
    } finally {
      setDeleting(false);
      setDeleteDialog(null);
    }
  };

  const handleDeleteClue = async () => {
    if (!deleteDialog || deleteDialog.type !== 'clue') return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('clues')
        .delete()
        .eq('id', deleteDialog.id);

      if (error) throw error;

      toast.success(`Ledtråden "${deleteDialog.name}" raderades`);
      
      // Update results
      setSearchResults(prev => prev.map(w => {
        if (w.id === deleteDialog.wordId) {
          return {
            ...w,
            clues: w.clues.filter(c => c.id !== deleteDialog.id)
          };
        }
        return w;
      }));
    } catch (error: any) {
      console.error('Delete clue error:', error);
      toast.error(`Kunde inte radera: ${error.message}`);
    } finally {
      setDeleting(false);
      setDeleteDialog(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <Search className="h-5 w-5" />
          Databashantering
        </CardTitle>
        <CardDescription>
          Sök efter ord eller ledtrådar för att redigera eller radera dem
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="search" className="sr-only">Sökterm</Label>
            <Input
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Sök ord eller ledtråd..."
              onKeyDown={(e) => e.key === 'Enter' && searchWords()}
            />
          </div>
          <Button onClick={searchWords} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Sök
          </Button>
        </div>

        {searchResults.length > 0 && (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {searchResults.length} resultat
            </p>
            
            {searchResults.map((word) => (
              <div key={word.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-lg">{word.word}</span>
                    <span className="text-muted-foreground ml-2">({word.length} bokstäver)</span>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialog({
                      type: 'word',
                      id: word.id,
                      name: word.word
                    })}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Radera ord
                  </Button>
                </div>
                
                {word.clues.length > 0 ? (
                  <div className="space-y-2 pl-4 border-l-2 border-muted">
                    <p className="text-sm font-medium text-muted-foreground">
                      Ledtrådar ({word.clues.length}):
                    </p>
                    {word.clues.map((clue) => (
                      <div key={clue.id} className="flex items-center justify-between gap-2 py-1">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-sm truncate">{clue.clue}</span>
                          <ClueLanguageFlagsEditor
                            clueId={clue.id}
                            language={clue.language}
                            onLanguageChange={(newLang) => {
                              setSearchResults(prev => prev.map(w => ({
                                ...w,
                                clues: w.clues.map(c => 
                                  c.id === clue.id ? { ...c, language: newLang } : c
                                )
                              })));
                            }}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                          onClick={() => setDeleteDialog({
                            type: 'clue',
                            id: clue.id,
                            name: clue.clue.substring(0, 30) + (clue.clue.length > 30 ? '...' : ''),
                            wordId: word.id
                          })}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic pl-4">
                    Inga ledtrådar
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <AlertDialog open={!!deleteDialog} onOpenChange={(open) => !open && setDeleteDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                Bekräfta radering
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteDialog?.type === 'word' ? (
                  <>
                    Är du säker på att du vill radera ordet <strong>"{deleteDialog.name}"</strong> och alla dess ledtrådar? 
                    Detta kan inte ångras.
                  </>
                ) : (
                  <>
                    Är du säker på att du vill radera ledtråden <strong>"{deleteDialog?.name}"</strong>? 
                    Detta kan inte ångras.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Avbryt</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteDialog?.type === 'word' ? handleDeleteWord : handleDeleteClue}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Radera
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
